// components/tags/TagsManager.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  IconPlus,
  IconSearch,
  IconFilter,
  IconShare,
  IconEdit,
  IconTrash,
  IconTag,
  IconUsers,
  IconEye,
  IconTrendingUp,
  IconRefresh,
  IconDownload,
  IconX,
} from "@tabler/icons-react";
import Swal from "sweetalert2";

import "./css/tags.css";
import TagModal from "./components/TagModal";
import { useTags } from "./hooks/useTags";

// Componente de Estatísticas
const TagsStats = ({ tags, loading }) => {
  const stats = {
    total: tags.length,
    shared: tags.filter((tag) => tag.is_shared).length,
    categories: [...new Set(tags.map((tag) => tag.category))].length,
    totalUsage: tags.reduce((sum, tag) => sum + (tag.usage_count || 0), 0),
  };

  if (loading) {
    return (
      <div className="tags-stats">
        <div className="stats-grid">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="stat-card">
              <div className="loading-skeleton" style={{ height: "60px" }}>
                <Spinner animation="border" size="sm" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tags-stats">
      <div className="stats-grid">
        <Card className="stat-card">
          <Card.Body className="text-center">
            <h3 className="stat-value text-primary">{stats.total}</h3>
            <p className="stat-label">Total de Tags</p>
          </Card.Body>
        </Card>

        <Card className="stat-card">
          <Card.Body className="text-center">
            <h3 className="stat-value text-success">{stats.shared}</h3>
            <p className="stat-label">Compartilhadas</p>
          </Card.Body>
        </Card>

        <Card className="stat-card">
          <Card.Body className="text-center">
            <h3 className="stat-value text-info">{stats.categories}</h3>
            <p className="stat-label">Categorias</p>
          </Card.Body>
        </Card>

        <Card className="stat-card">
          <Card.Body className="text-center">
            <h3 className="stat-value text-warning">{stats.totalUsage}</h3>
            <p className="stat-label">Total de Usos</p>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

// Componente de Item de Tag
const TagItem = ({ tag, onEdit, onDelete, onToggleShare }) => {
  const getCategoryIcon = (category) => {
    const icons = {
      general: "🏷️",
      client: "👤",
      contact: "📞",
      appointment: "📅",
      chat: "💬",
    };
    return icons[category] || "🏷️";
  };

  const getCategoryLabel = (category) => {
    const labels = {
      appointment: "Agendamento",
      service: "Atendimento",
      client: "Cliente",
      contact: "Contato",
      chat: "Chat",
      funnel: "Funil",
      general: "Geral",
    };
    return labels[category] || "Geral";
  };

  const handleToggleShare = async () => {
    if (!tag.can_share) {
      Swal.fire({
        title: "Ação não permitida",
        text: "Apenas o criador da tag pode alterar o compartilhamento.",
        icon: "warning",
        confirmButtonText: "Entendi",
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
      return;
    }

    try {
      const action = tag.is_shared ? "tornar privada" : "compartilhar";
      const result = await Swal.fire({
        title: `${action.charAt(0).toUpperCase() + action.slice(1)} Tag`,
        text: `Deseja ${action} a tag "${tag.name}"?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: action.charAt(0).toUpperCase() + action.slice(1),
        cancelButtonText: "Cancelar",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-secondary",
        },
      });

      if (result.isConfirmed) {
        await onToggleShare(tag.id);

        Swal.fire({
          title: "Sucesso!",
          text: `Tag ${
            tag.is_shared ? "privada" : "compartilhada"
          } com sucesso!`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: error.message || "Erro ao alterar compartilhamento da tag",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-danger",
        },
      });
    }
  };

  const handleDelete = async () => {
    if (!tag.can_delete) {
      Swal.fire({
        title: "Ação não permitida",
        text: "Apenas o criador da tag pode excluí-la.",
        icon: "warning",
        confirmButtonText: "Entendi",
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
      return;
    }

    try {
      const result = await Swal.fire({
        title: "Excluir Tag",
        text: `Deseja excluir a tag "${tag.name}"? Esta ação não pode ser desfeita.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-secondary",
        },
      });

      if (result.isConfirmed) {
        await onDelete(tag.id);

        Swal.fire({
          title: "Excluída!",
          text: "Tag excluída com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: error.message || "Erro ao excluir tag",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-danger",
        },
      });
    }
  };

  return (
    <div className="tag-item">
      <div className="tag-content">
        <div className="tag-info">
          <div className="tag-main">
            <div className="tag-color" style={{ backgroundColor: tag.color }} />
            <h5 className="tag-name">{tag.name}</h5>
          </div>

          <div className="tag-meta">
            <Badge bg="secondary" className="tag-badge category">
              {getCategoryIcon(tag.category)} {getCategoryLabel(tag.category)}
            </Badge>

            {tag.is_shared && (
              <Badge bg="success" className="tag-badge shared">
                <IconShare size={12} className="me-1" />
                Compartilhada
              </Badge>
            )}

            <Badge bg="info" className="tag-badge usage">
              <IconTrendingUp size={12} className="me-1" />
              {tag.usage_count || 0} usos
            </Badge>
          </div>

          {tag.description && (
            <p className="tag-description">{tag.description}</p>
          )}

          {/* <div className="tag-details">
            <small className="text-muted">
              Criada por: <strong>{tag.created_by_name}</strong>
            </small>
          </div> */}
        </div>

        <div className="tag-actions">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Compartilhar/Privar</Tooltip>}
          >
            <Button
              variant={tag.is_shared ? "success" : "outline-success"}
              size="sm"
              className="tag-action-btn share"
              onClick={handleToggleShare}
              disabled={!tag.can_share}
            >
              <IconShare size={17} />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger placement="top" overlay={<Tooltip>Editar</Tooltip>}>
            <Button
              variant="warning"
              size="sm"
              className="tag-action-btn edit"
              onClick={() => onEdit(tag)}
              disabled={!tag.can_edit}
            >
              <IconEdit size={17} />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger placement="top" overlay={<Tooltip>Excluir</Tooltip>}>
            <Button
              variant="danger"
              size="sm"
              className="tag-action-btn delete"
              onClick={handleDelete}
              disabled={!tag.can_delete}
            >
              <IconTrash size={17} />
            </Button>
          </OverlayTrigger>
        </div>
      </div>
    </div>
  );
};

// Componente Principal
const TagsManager = () => {
  const {
    tags,
    loading,
    error,
    filters,
    createTag,
    updateTag,
    deleteTag,
    toggleShare,
    applyFilters,
    clearFilters,
    refreshTags,
    setError,
  } = useTags();

  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sharedFilter, setSharedFilter] = useState("");
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Limpar erro após 5 segundos
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, setError]);

  // Marcar que não é mais o mount inicial após primeira renderização
  useEffect(() => {
    setIsInitialMount(false);
  }, []);

  // Aplicar filtros com debounce (apenas após o mount inicial)
  useEffect(() => {
    // Evitar chamada duplicada no mount inicial
    if (isInitialMount) return;

    const timeoutId = setTimeout(() => {
      const newFilters = {};
      if (searchTerm) newFilters.search = searchTerm;
      if (categoryFilter) newFilters.category = categoryFilter;
      if (sharedFilter !== "") newFilters.is_shared = sharedFilter === "true";

      applyFilters(newFilters);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, categoryFilter, sharedFilter, applyFilters, isInitialMount]);

  const handleCreateTag = () => {
    setEditingTag(null);
    setModalError(null);
    setShowModal(true);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setModalError(null);
    setShowModal(true);
  };

  const handleSaveTag = async (tagData) => {
    setModalLoading(true);
    setModalError(null);

    try {
      if (editingTag) {
        await updateTag(editingTag.id, tagData);
      } else {
        await createTag(tagData);
      }
      setShowModal(false);
    } catch (err) {
      setModalError(err.message || "Erro ao salvar tag");
      throw err;
    } finally {
      setModalLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setSharedFilter("");
    clearFilters();
  };

  const hasActiveFilters = searchTerm || categoryFilter || sharedFilter !== "";

  return (
    <div className="main-container">
      {/* Header */}
      <div className="tags-header">
        <Row>
          <Col>
            <h4 className="d-flex align-items-center gap-2">
              <IconTag size={24} />
              Gerenciar Tags
            </h4>
            <p className="mb-0">
              Organize e gerencie suas tags para categorizar clientes, contatos,
              agendamentos e conversas.
            </p>
          </Col>
        </Row>
      </div>

      {/* Alert de Erro */}
      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          className="mb-3"
        >
          <strong>Erro:</strong> {error}
        </Alert>
      )}

      {/* Estatísticas */}
      <TagsStats tags={tags} loading={loading} />

      {/* Filtros e Ações */}
      <Card className="tags-actions">
        <Card.Body>
          <Row className="filters-row">
            <Col lg={3} md={6} className="filter-group">
              <Form.Label>Buscar Tags</Form.Label>
              <div className="search-input">
                <IconSearch className="search-icon" size={17} />
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </Col>

            <Col lg={2} md={6} className="filter-group">
              <Form.Label>Categoria</Form.Label>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Todas</option>
                {/* <option value="service">Atendimento</option> */}
                <option value="appointment"> Agendamento</option>

                <option value="client">Cliente</option>
                <option value="contact"> Contato</option>

                <option value="chat">Chat</option>
                {/* <option value="funnel"> Funil</option> */}
                <option value="general"> Geral</option>
              </Form.Select>
            </Col>

            <Col lg={2} md={6} className="filter-group">
              <Form.Label>Compartilhamento</Form.Label>
              <Form.Select
                value={sharedFilter}
                onChange={(e) => setSharedFilter(e.target.value)}
              >
                <option value="">Todas</option>
                <option value="true">Compartilhadas</option>
                <option value="false">Privadas</option>
              </Form.Select>
            </Col>

            <Col lg={5} md={6} className="filter-group">
              <Form.Label>&nbsp;</Form.Label>
              <div className="actions-buttons">
                {hasActiveFilters && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearFilters}
                    className="d-flex align-items-center gap-2"
                  >
                    <IconX size={17} />
                    Limpar
                  </Button>
                )}

                <Button
                  variant="outline-secondary"
                  onClick={refreshTags}
                  disabled={loading}
                  className="d-flex align-items-center gap-2"
                >
                  <IconRefresh size={17} />
                  Atualizar
                </Button>

                <Button
                  variant="primary"
                  onClick={handleCreateTag}
                  className="d-flex align-items-center gap-2"
                >
                  <IconPlus size={17} />
                  Nova Tag
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de Tags */}
      <Card className="tags-grid">
        <div className="tags-grid-header">
          <h4 className="tags-grid-title">Tags</h4>
          <Badge className="tags-count">
            {loading ? "..." : tags.length} {tags.length === 1 ? "tag" : "tags"}
          </Badge>
        </div>

        <div className="tags-list">
          {loading ? (
            <div className="loading-state">
              <Spinner animation="border" className="loading-spinner" />
              <p>Carregando tags...</p>
            </div>
          ) : tags.length === 0 ? (
            <div className="empty-state">
              <IconTag className="empty-state-icon" />
              <h4>Nenhuma tag encontrada</h4>
              <p>
                {hasActiveFilters
                  ? "Tente ajustar os filtros ou limpar a busca."
                  : "Comece criando sua primeira tag para organizar seus dados."}
              </p>
              {!hasActiveFilters && (
                <div className="w-100 d-flex justify-content-center">
                  <Button
                    variant="primary"
                    onClick={handleCreateTag}
                    className="d-flex align-items-center gap-2"
                  >
                    <IconPlus size={17} />
                    Criar Primeira Tag
                  </Button>
                </div>
              )}
            </div>
          ) : (
            tags.map((tag, index) => (
              <TagItem
                key={index}
                tag={tag}
                onEdit={handleEditTag}
                onDelete={deleteTag}
                onToggleShare={toggleShare}
              />
            ))
          )}
        </div>
      </Card>

      {/* Modal de Criação/Edição */}
      <TagModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSaveTag}
        tag={editingTag}
        loading={modalLoading}
        error={modalError}
      />
    </div>
  );
};

export default TagsManager;
